import { useTranslation } from 'react-i18next';

import AlertLine from '@/assets/icons/ri_alert_line.svg?component';
import { Alert } from '@/components/atoms/Alert';
import { Spinner } from '@/components/atoms/Spinner';
import { SvgIcon } from '@/components/atoms/SvgIcon/SvgIcon';
import PageLayout from '@/components/Layout/PageLayout';
import ContentBlock from '@/components/molecules/ContentBlock';
import { useCageName } from '@/contexts/cage';
import type { CageWinchMeta } from '@/domain/cageWinch';

import { LivePosition } from './components/LivePosition/LivePosition';
import { MeasurementCard } from './components/MeasurementCard/MeasurementCard';
import { ServiceCard } from './components/ServiceCard/ServiceCard';
import { WinchInfoBar } from './components/WinchInfoBar/WinchInfoBar';
import { useCageWinchControl } from './hooks/useCageWinchControl';
import type { WinchMqttUser } from './winchMqttApi';

import styles from './CageWinchControl.module.scss';

interface CageWinchControlPageContentProps {
    winchData: CageWinchMeta | null;
    user: WinchMqttUser;
}

export default function CageWinchControlPageContent({
    winchData,
    user,
}: CageWinchControlPageContentProps) {
    const { t } = useTranslation();
    const cageName = useCageName();
    const control = useCageWinchControl(winchData, user);
    const { winch, measurement, verticalScan, limits } = control;
    const waitingForBroker = Boolean(winchData?.winchExists) && !winch.online && winch.awaitingLive;

    return (
        <PageLayout title={cageName} subtitle={t('winchControl.page.subtitle')}>
            <div className={styles.page}>
                <div className={styles.toolbar}>
                    <WinchInfoBar
                        bioscopeId={winchData?.bioscopeId}
                        winchId={winchData?.winchId}
                        distance={winch.telemetry?.distance ?? null}
                        depth={winch.telemetry?.depth ?? null}
                        online={winch.online}
                        moving={winch.moving}
                        calibrated={winch.calibrated}
                        showStop={control.scanRunning || winch.moving}
                        stopTooltip={
                            control.scanRunning
                                ? t('winchControl.button.stopScanTooltip')
                                : t('winchControl.button.stopTooltip')
                        }
                        onStop={control.stopWinch}
                    />
                </div>
                <div className={styles.workspace}>
                    <div className={styles.graphicColumn}>
                        {!waitingForBroker && (!winchData?.winchExists || !winch.online) && (
                            <ContentBlock shrink>
                                <Alert
                                    tone="warning"
                                    live="polite"
                                    icon={<SvgIcon icon={AlertLine} color="currentColor" />}>
                                    {t('winchControl.offline.message')}
                                </Alert>
                            </ContentBlock>
                        )}
                        {waitingForBroker ||
                        (winch.awaitingLive &&
                            winch.online &&
                            winchData?.winchExists &&
                            !control.isReady) ? (
                            <Spinner size="lg" />
                        ) : (
                            <LivePosition
                                distance={winch.telemetry?.distance ?? null}
                                depth={winch.telemetry?.depth ?? null}
                                targetDistance={winch.telemetry?.targetDistance ?? null}
                                targetDepth={winch.telemetry?.targetDepth ?? null}
                                previewDistance={
                                    control.hasEditedMeasurement ? measurement.distance : null
                                }
                                previewDepth={
                                    control.hasEditedMeasurement ? measurement.depth : null
                                }
                                moving={winch.moving}
                                distanceMax={limits.distance.max}
                                depthMax={limits.depth.max}
                            />
                        )}
                    </div>
                    {control.isReady && winch.online && winchData?.winchExists && (
                        <div className={styles.controls}>
                            <MeasurementCard
                                distance={measurement.distance}
                                depth={measurement.depth}
                                minDepth={verticalScan.minDepth}
                                maxDepth={verticalScan.maxDepth}
                                scanEnabled={verticalScan.enabled}
                                scanRunning={control.scanRunning}
                                canMove={control.canMoveSelected}
                                moveDisabledReason={control.moveDisabledReason}
                                distanceLimits={limits.distance}
                                depthLimits={limits.depth}
                                onSave={control.saveMeasurement}
                                onToggleScan={control.toggleScan}
                                onMove={control.moveMeasurement}
                                onStop={control.stopWinch}
                            />
                            <ServiceCard
                                distance={control.service?.distance ?? null}
                                depth={control.service?.depth ?? null}
                                canMove={control.canMoveService}
                                moveDisabledReason={control.serviceMoveDisabledReason}
                                onMove={control.moveToService}
                            />
                        </div>
                    )}
                </div>
            </div>
        </PageLayout>
    );
}

export { CageWinchControlPageContent };
